import React, { useLayoutEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import toast, { Toaster } from 'react-hot-toast';
import './index.css';
import './components/AgreementTypography.css';
import App from './App';
import { registerServiceWorker } from './registerServiceWorker';
import { bootstrapAnalyticsFromStoredConsent } from './analytics';
// Import early to set up beforeinstallprompt listener before React components mount
import './hooks/usePwaInstallPrompt';

// Apply Consent Mode defaults and only load GA if analytics cookies were previously accepted.
bootstrapAnalyticsFromStoredConsent();

// Apply theme class from localStorage as early as possible to avoid purple flash on refresh
try {
  const storedTheme = localStorage.getItem('preferedTheme');
  const saved = localStorage.getItem('user');
  const parsed = saved ? JSON.parse(saved) : null;
  const theme = storedTheme || parsed?.preferedTheme;
  const rootEl = document.body;
  const classes = ['theme-neutral', 'theme-purple', 'theme-pink', 'theme-baby-blue'];
  rootEl.classList.remove(...classes);
  if (theme === 'neutral') rootEl.classList.add('theme-neutral');
  else if (theme === 'pink') rootEl.classList.add('theme-pink');
  else if (theme === 'baby-blue') rootEl.classList.add('theme-baby-blue');
  else rootEl.classList.add('theme-purple');
} catch {}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ShowAppWhenReady({ children }: { children: React.ReactNode }) {
  useLayoutEffect(() => {
    const rootEl = document.getElementById('root');
    if (rootEl) rootEl.style.visibility = 'visible';
  }, []);
  return <>{children}</>;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ShowAppWhenReady>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
    </ShowAppWhenReady>
  </React.StrictMode>
); 

// PWA/service worker handling should only run in production.
// In development, explicitly unregister any existing service workers so they
// cannot cause reload loops or stale-asset behavior on localhost.
const isRunningAsPWA =
  window.matchMedia?.('(display-mode: standalone)')?.matches ||
  // iOS Safari "Add to Home Screen"
  (window.navigator as any).standalone === true;

if ('serviceWorker' in navigator) {
  if (process.env.NODE_ENV === 'production') {
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  } else {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister().catch(() => {});
      });
    }).catch(() => {});
  }
}

registerServiceWorker((registration) => {
  // Only prompt users running the installed PWA, not regular browser tabs.
  if (!isRunningAsPWA) return;

  toast(
    (t) => (
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', maxWidth: 520 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Update available</div>
          <div style={{ opacity: 0.9, fontSize: 13 }}>
            A new version is ready. Update now (recommended), or close and reopen the app later.
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            toast.dismiss(t.id);
            registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
            if (!registration.waiting) window.location.reload();
          }}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            background: '#0B4778',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Update
        </button>
      </div>
    ),
    { duration: Infinity }
  );
});